import { Factory } from "@/data/dao/Factory";
import { SupportDao } from "@/data/dao/service/SupportDao";
import { IssueState } from "@/data/model/Enums";
import type { IIssue } from "@/data/model/Support";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";

import { i18n } from "@/portal/Trans";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { TableFilter } from "@/portal/elements/TableFilter";
import { SupportIssueWindow } from "@/portal/modal/SupportIssueWindow";

export class IssueTableController {
    private $element: JQuery;
    private dao: SupportDao;

    private dataTable: DataTable<IIssue>;
    private search: TableFilter;

    constructor(element: JQuery) {
        this.$element = element;

        this.dao = Factory.getInstance<SupportDao>(SupportDao);

        const tableConfig: IDataTableConfig<IIssue> = {
            columnMapping: [
                {
                    name: i18n("general.fields.state"),
                    property: "state",
                    styles: { width: "10%" },
                    formatter: (m, p) => {
                        let classN = "success";
                        switch (p) {
                            case IssueState.Open:
                                classN = "danger";
                                break;
                            case IssueState.InProgress:
                                classN = "warning";
                                break;
                        }

                        return $(
                            '<div class="kt-badge kt-badge--' +
                                classN +
                                ' kt-badge--inline">' +
                                i18n("enums.IssueState." + IssueState[p]) +
                                "</div>"
                        );
                    },
                },
                {
                    name: i18n("issue.fields.received"),
                    property: "received",
                    styles: { width: "12%" },
                    formatter: (m, p) => DateTime.toPreciseDateTimeString(p),
                },
                {
                    name: i18n("issue.fields.lastModified"),
                    property: "lastModified",
                    styles: { width: "12%" },
                    formatter: (m, p) =>
                        p === undefined ? i18n("general.timeFormat.notYet") : DateTime.toPreciseDateTimeString(p),
                },
                {
                    name: i18n("general.entities.user"),
                    property: "userId",
                    styles: { width: "12%" },
                    formatter: (m, p) => {
                        if (p === undefined) return Format.htmlEscape(m.firstName + " " + m.lastName);

                        return $(
                            '<a href="/user/' + p + '">' + Format.htmlEscape(m.firstName + " " + m.lastName) + "</a>"
                        ).click((e) => {
                            e.stopPropagation();
                        });
                    },
                },
                {
                    name: i18n("general.fields.email"),
                    property: "email",
                    styles: { width: "11%" },
                },
                {
                    name: i18n("general.fields.phone"),
                    property: "phoneNumber",
                    styles: { width: "11%" },
                },
                {
                    name: i18n("issue.fields.message"),
                    property: "message",
                },
                {
                    name: "",
                    property: "id",
                    styles: { width: "110px", "text-align": "right" },
                    formatter: () => {
                        return $(
                            '<span><a class="btn btn-sm btn-clean btn-icon btn-icon-md"><i class="la la-edit"></i></a></span>'
                        );
                    },
                },
            ],
            defaultSortAsc: false,
            defaultSortProperty: "received",
            filterProvider: {
                getFilter: () => this.search.getValue(),
                setFilter: (d) => this.search.setValue(d),
            },
        };

        this.search = new TableFilter().appendTo(this.$element.find("> .kt-portlet__body.tablefilter"));
        this.search.addDropdownFilterFromEnum("state", IssueState, "enums.IssueState", i18n("issue.allStates"));
        this.dataTable = new DataTable<IIssue>(
            this.$element.find("> .kt-portlet__body > .kt-datatable"),
            this.dao.issueDataSource(),
            tableConfig
        );
        this.dataTable.onClick((m) => {
            this.onEditIssue(m);
        });

        this.dataTable.listenTo(this.search);
    }

    private onEditIssue(issue: IIssue) {
        const modal = new SupportIssueWindow(issue);
        modal.onSaved = () => this.dataTable.loadData();
        modal.onRemoved = () => this.dataTable.loadData(true);

        modal.show();
    }
}
